import { useEffect, useState } from 'react'
import { HelpTutorial, formatTutorialDuration, getTutorialPosterCandidates } from './tutorials'

type TutorialCardProps = {
  tutorial: HelpTutorial
  onPlay: (tutorial: HelpTutorial) => void
  compact?: boolean
}

const PlayIcon = ({ compact = false }: { compact?: boolean }) => (
  <span
    aria-hidden="true"
    className={`font-icon-solid flex shrink-0 items-center justify-center rounded-full border border-white/60 bg-gray-900/85 pl-[2px] text-white shadow-lg transition-transform group-hover:scale-105 ${
      compact ? 'h-[44px] w-[44px] text-[16px]' : 'h-[64px] w-[64px] text-[24px]'
    }`}
  >
    
  </span>
)

const TutorialCard = ({ tutorial, onPlay, compact = false }: TutorialCardProps) => {
  // Candidates run best-quality first; a failed load steps down to the next one.
  const candidates = getTutorialPosterCandidates(tutorial)
  const [posterIndex, setPosterIndex] = useState(0)
  const poster = candidates[Math.min(posterIndex, candidates.length - 1)]

  useEffect(() => setPosterIndex(0), [tutorial.id])

  const handlePosterError = () => setPosterIndex((index) => Math.min(index + 1, candidates.length - 1))

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => onPlay(tutorial)}
        aria-label={`Open ${tutorial.title} tutorial`}
        className="group flex w-full max-w-[720px] items-center gap-md overflow-hidden rounded-md border border-primary-500 bg-transparent p-sm text-left transition-colors hover:border-primary-600 hover:bg-primary-50"
      >
        <span className="relative block aspect-video w-[180px] shrink-0 overflow-hidden rounded-sm bg-gray-900">
          <img src={poster} onError={handlePosterError} alt="" loading="lazy" className="h-full w-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center bg-gray-900/15 transition-colors group-hover:bg-gray-900/25">
            <PlayIcon compact />
          </span>
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-xs">
          <span className="text-title-md text-gray-900">{tutorial.title}</span>
          <span className="text-text-sm leading-relaxed text-gray-500">{tutorial.description}</span>
          <span className="mt-[5px] text-text-sm font-medium text-gray-900">
            Play tutorial ({formatTutorialDuration(tutorial.durationSeconds)})
          </span>
        </span>
      </button>
    )
  }

  return (
    <article className="overflow-hidden rounded-md border border-primary-400 bg-transparent transition-colors hover:border-primary-500 hover:bg-primary-50">
      <button
        type="button"
        aria-label={`Open ${tutorial.title} tutorial`}
        onClick={() => onPlay(tutorial)}
        className="group relative block aspect-video w-full overflow-hidden bg-gray-900 text-left"
      >
        <img
          src={poster}
          onError={handlePosterError}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.01]"
        />
        <span className="absolute inset-0 flex items-center justify-center bg-gray-900/10 transition-colors group-hover:bg-gray-900/25">
          <PlayIcon />
        </span>
      </button>

      <div className="flex items-start gap-md p-md">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-md">
            <h3 className="text-title-md text-gray-900">{tutorial.title}</h3>
            <span className="shrink-0 text-text-sm text-gray-400">
              {formatTutorialDuration(tutorial.durationSeconds)}
            </span>
          </div>
          <p className="mt-xs text-text-sm leading-relaxed text-gray-500">{tutorial.description}</p>
        </div>
      </div>
    </article>
  )
}

export default TutorialCard
